import {
  Checkbox,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import { X as XIcon } from "lucide-react";
import * as React from "react";

import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";

import {
  type FilterOption,
  type FilterOptions,
  type FilterOptionsResolver,
  type LabeledOption,
  type ResolvedOptions,
} from "../FilterDefinition";
import {
  type EnumComparator,
  type SetComparator,
  isValueless,
} from "../FilterSchema";

import styles from "./OptionsValueInput.module.css";

// Trailing debounce applied to the search input before a server-searched
// resolver is re-invoked, so a burst of typing collapses to one query. Matches
// the convention used by other server-search inputs (e.g. `OrgAutocomplete`).
const SEARCH_DEBOUNCE_MS = 300;

// Shortest search term worth sending to a server resolver. Postgres can
// extract no trigram from a 1-2 character `%term%`, so those searches
// degenerate to a full index scan — slower than having no index at all. The
// empty search stays allowed: it is the intentional first-page-on-open query.
const MIN_SERVER_SEARCH_LENGTH = 3;

// Fixed height (px) of one option row. Rows are single-line — the label
// truncates with an ellipsis (see `.row-label`) — so a constant estimate is
// exact and the virtualizer never needs to re-measure. This constant is the
// single source of the row height: each row's inline `height` style derives
// from it (the stylesheet declares none).
const OPTION_ROW_HEIGHT = 32;

// Rows rendered outside the viewport on each side, trading a little extra DOM
// for scroll headroom so fast scrolling doesn't flash blank rows.
const OPTION_OVERSCAN = 8;

interface OptionsValueInputProps {
  comparator: SetComparator | EnumComparator;
  /** The filter field this picker edits (e.g. ``"tags"``); keys the option query. */
  field: string;
  /**
   * The selected options — wire ``value`` plus the display ``label`` the user
   * saw when picking each one. Carrying the label in the selection is what
   * lets chips and pinned rows render labels for server-searched options
   * whose full option list isn't available later.
   */
  value: LabeledOption[];
  /** Fired on every selection change with the next selected options. */
  onChange: (next: LabeledOption[]) => void;
  options: FilterOptions;
}

function normalizeOption(option: FilterOption): LabeledOption {
  return typeof option === "string" ? { label: option, value: option } : option;
}

/**
 * Searchable, multi-select checkbox list shared by the ``set`` and ``enum``
 * filter types. Valueless comparators (``IS_NULL`` / ``IS_NOT_NULL``) render
 * nothing — redundant with ``FilterForm``, which already hides the value row
 * for them.
 *
 * ``options`` drives one of two modes:
 *
 * - **Static array** (``string[]`` or ``LabeledOption[]``): the whole list is
 *   held in memory and filtered CLIENT-SIDE by label substring as the user
 *   types. No async, no debounce.
 * - **Server-searched resolver** (function form, see
 *   {@link FilterOptionsResolver}): the resolver is invoked with the empty
 *   search on open and re-invoked with the DEBOUNCED (300 ms) search text on
 *   each keystroke. Results are SERVER-AUTHORITATIVE — rendered in the returned
 *   order with NO client-side filtering. A loading hint shows while a search is
 *   in flight without blanking the previously-rendered options
 *   (``keepPreviousData``), and selected values absent from the current results
 *   are pinned at the top so they stay visible and deselectable.
 *
 * Both modes report selection changes through ``onChange`` as labeled
 * options, so every selected value carries its own display label.
 */
export function OptionsValueInput({
  comparator,
  field,
  value,
  onChange,
  options,
}: OptionsValueInputProps) {
  const [searchValue, setSearchValue] = React.useState("");
  // The input stays immediate; the server path searches the settled term.
  // Trimmed so a trailing space can't turn an exact match into `%acme %`.
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const debounceSearch = useDebouncedCallback(
    setDebouncedSearch,
    SEARCH_DEBOUNCE_MS,
  );
  // Threaded down to whichever list variant is active, all the way to the
  // `role="listbox"` element itself, so ArrowDown can focus it directly
  // instead of guessing at its DOM position via `querySelector`.
  const listboxRef = React.useRef<HTMLDivElement>(null);

  if (isValueless(comparator)) {
    return null;
  }

  const handleSearchChange = (next: string) => {
    setSearchValue(next);
    debounceSearch(next.trim());
  };

  return (
    <div className={styles.input}>
      {typeof options === "function" ? (
        <ServerOptionList
          field={field}
          resolver={options}
          search={debouncedSearch}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          value={value}
          onChange={onChange}
          listboxRef={listboxRef}
        />
      ) : (
        <StaticOptionList
          options={options}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          value={value}
          onChange={onChange}
          listboxRef={listboxRef}
        />
      )}
    </div>
  );
}

/**
 * What a list variant feeds the shared ``OptionList``: the current result set
 * (in render order) plus its state flags. Splitting by SOURCE — rather than
 * having each variant assemble its own rows — is what guarantees pinning and
 * the loading/error/empty/truncation branches exist on both paths.
 */
interface OptionSource {
  options: LabeledOption[];
  isLoading: boolean;
  isError: boolean;
  truncated: boolean;
}

/**
 * Static, in-memory option source — filtered CLIENT-SIDE by label substring,
 * synchronously (which is why the array isn't just wrapped in a resolver).
 * Search matches ``label`` (what the user sees), never the opaque wire
 * ``value``. Consumers must keep the set small enough to hold in memory.
 */
function useStaticOptions(
  options: string[] | LabeledOption[],
  searchValue: string,
): OptionSource {
  const normalized = React.useMemo(
    () => (options as FilterOption[]).map(normalizeOption),
    [options],
  );
  const filtered = React.useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (needle === "") {
      return normalized;
    }
    return normalized.filter((option) =>
      option.label.toLowerCase().includes(needle),
    );
  }, [normalized, searchValue]);

  return React.useMemo(
    () => ({
      options: filtered,
      isLoading: false,
      isError: false,
      truncated: false,
    }),
    [filtered],
  );
}

/**
 * Server-searched option source. ``search`` arrives already debounced and
 * trimmed; this queries the resolver for it (via ``useQuery`` with
 * ``keepPreviousData`` so the list doesn't blank on each keystroke). Results
 * are SERVER-AUTHORITATIVE — returned in order with no client-side filtering.
 */
function useServerOptions(
  field: string,
  resolver: FilterOptionsResolver,
  search: string,
): OptionSource {
  const query = useQuery({
    // Retention is deliberately off: it belongs to the resolver, which is the
    // only layer that can see the endpoint and org behind it.
    queryKey: ["filter-options", field, search],
    queryFn: ({ signal }) => resolver(search, signal),
    gcTime: 0,
    staleTime: 0,
    // Hold back terms too short to search efficiently server-side; the
    // previous page stays visible via `placeholderData` until the term grows.
    enabled: search.length === 0 || search.length >= MIN_SERVER_SEARCH_LENGTH,
    // Keep the last page visible while the next search is in flight so the list
    // doesn't blank on every keystroke.
    placeholderData: keepPreviousData,
  });

  // A resolver may return a bare list (complete, no truncation) or a
  // `{ options, truncated }` object; normalize to the object shape. Memoized
  // so the no-data fallback keeps a stable identity — `options` (and the
  // pinning effect keyed on it) must only change when the RESULTS change.
  const resolved: ResolvedOptions = React.useMemo(() => {
    if (Array.isArray(query.data)) {
      return { options: query.data, truncated: false };
    }
    return query.data ?? { options: [], truncated: false };
  }, [query.data]);

  const options = React.useMemo(
    () => resolved.options.map(normalizeOption),
    [resolved.options],
  );

  return React.useMemo(
    () => ({
      options,
      isLoading: query.isFetching,
      isError: query.isError,
      truncated: resolved.truncated ?? false,
    }),
    [options, query.isFetching, query.isError, resolved.truncated],
  );
}

interface OptionListProps {
  source: OptionSource;
  searchValue: string;
  onSearchChange: (next: string) => void;
  value: LabeledOption[];
  onChange: (next: LabeledOption[]) => void;
  listboxRef: React.RefObject<HTMLDivElement>;
}

/**
 * Shared row assembly and state branches for BOTH option sources: pins
 * selected-but-absent options, renders the loading / error / empty /
 * truncation states, and delegates row rendering to ``OptionListbox``.
 *
 * Owns the search input too, so the in-flight indicator can live inside it
 * rather than as a block that reflows the list (see ``SearchInput``).
 */
function OptionList({
  source,
  searchValue,
  onSearchChange,
  value,
  onChange,
  listboxRef,
}: OptionListProps) {
  // Pin selected options missing from the current result set so they stay
  // visible and deselectable after the search moves away. Each selection
  // carries its own label, so no lookup is needed.
  //
  // The pinned set is a SNAPSHOT taken when the result set changes, not a
  // per-render derivation from the live selection. Deriving it live would
  // unmount a pinned row the instant the user unchecks it — dropping keyboard
  // focus to <body> and leaving no way to undo the click short of retyping
  // the search that surfaced the row. A snapshot keeps the row mounted
  // (unchecked) until the next search moves the results on.
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const [pinned, setPinned] = React.useState<LabeledOption[]>([]);
  // NUL-joined and keyed on result CONTENT, not array identity: a parent re-rendering with a
  // freshly-built (but equal) options array must not re-derive the pinned set
  // mid-toggle, or the snapshot degenerates to the live derivation it exists
  // to avoid.
  const resultsKey = React.useMemo(
    () => source.options.map((option) => option.value).join("\u0000"),
    [source.options],
  );
  React.useEffect(() => {
    const inResults = new Set(source.options.map((option) => option.value));
    setPinned(
      valueRef.current.filter((option) => !inResults.has(option.value)),
    );
    // `source.options` intentionally omitted: `resultsKey` is derived from it
    // and only changes when the option VALUES change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsKey]);

  const rows = [...pinned, ...source.options];

  return (
    <>
      <SearchInput
        value={searchValue}
        onChange={onSearchChange}
        isLoading={source.isLoading}
        listboxRef={listboxRef}
      />
      {source.isError && <ErrorState />}
      {rows.length > 0 ? (
        <OptionListbox
          options={rows}
          value={value}
          onToggle={(option) => onChange(toggleOption(value, option))}
          listboxRef={listboxRef}
          truncated={source.truncated && !source.isError}
          resultCount={source.options.length}
        />
      ) : (
        !source.isLoading && !source.isError && <EmptyState />
      )}
    </>
  );
}

function SearchInput({
  value,
  onChange,
  isLoading,
  listboxRef,
}: {
  value: string;
  onChange: (next: string) => void;
  isLoading: boolean;
  listboxRef: React.RefObject<HTMLDivElement>;
}) {
  let adornment: React.ReactNode = null;
  if (isLoading) {
    adornment = <CircularProgress size={14} />;
  } else if (value.length > 0) {
    adornment = (
      <button
        type="button"
        className={styles.searchClear}
        onClick={() => onChange("")}
        aria-label="Clear search"
      >
        <XIcon size={14} />
      </button>
    );
  }

  return (
    <>
      <TextField
        autoComplete="off"
        size="small"
        type="search"
        className={styles.searchInput}
        placeholder="Search options..."
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ "aria-label": "Search options" }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">{adornment}</InputAdornment>
          ),
        }}
        // The combobox affordance: ArrowDown hands focus to the option
        // listbox (a sibling rendered by whichever list variant is active),
        // whose own keyboard model takes over from there.
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            listboxRef.current?.focus();
          }
        }}
      />
      {/* The spinner is decorative; this carries the same news to screen
          readers, which the removed block-level loading row used to. */}
      <span className="sr-only" role="status" aria-live="polite">
        {isLoading ? "Loading options…" : ""}
      </span>
    </>
  );
}

interface StaticOptionListProps {
  options: string[] | LabeledOption[];
  searchValue: string;
  onSearchChange: (next: string) => void;
  value: LabeledOption[];
  onChange: (next: LabeledOption[]) => void;
  listboxRef: React.RefObject<HTMLDivElement>;
}

function StaticOptionList({
  options,
  searchValue,
  onSearchChange,
  value,
  onChange,
  listboxRef,
}: StaticOptionListProps) {
  const source = useStaticOptions(options, searchValue);
  return (
    <OptionList
      source={source}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      value={value}
      onChange={onChange}
      listboxRef={listboxRef}
    />
  );
}

interface ServerOptionListProps {
  field: string;
  resolver: FilterOptionsResolver;
  /** Already debounced and trimmed by `OptionsValueInput`. */
  search: string;
  /** The immediate input text — `search` is its settled form. */
  searchValue: string;
  onSearchChange: (next: string) => void;
  value: LabeledOption[];
  onChange: (next: LabeledOption[]) => void;
  listboxRef: React.RefObject<HTMLDivElement>;
}

function ServerOptionList({
  field,
  resolver,
  search,
  searchValue,
  onSearchChange,
  value,
  onChange,
  listboxRef,
}: ServerOptionListProps) {
  const source = useServerOptions(field, resolver, search);
  return (
    <OptionList
      source={source}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      value={value}
      onChange={onChange}
      listboxRef={listboxRef}
    />
  );
}

/**
 * The next selection after toggling ``option``: removed if already selected
 * (matched by wire ``value``), appended — label and all — if not.
 */
function toggleOption(
  current: LabeledOption[],
  option: LabeledOption,
): LabeledOption[] {
  return current.some((selected) => selected.value === option.value)
    ? current.filter((selected) => selected.value !== option.value)
    : [...current, option];
}

interface OptionListboxProps {
  options: LabeledOption[];
  value: LabeledOption[];
  onToggle: (option: LabeledOption) => void;
  /** Threaded down from `OptionsValueInput` so ArrowDown in the search input
   * can focus this element directly (see the container's `onKeyDown`). */
  listboxRef: React.RefObject<HTMLDivElement>;
  /** Whether the server capped the result set. */
  truncated: boolean;
  /** Server rows only (pinned selections excluded) — what the cap applied to. */
  resultCount: number;
}

/**
 * Renders the option rows, virtualized: only the rows within (and just
 * outside) the scroll viewport are mounted, so a large option list stays cheap
 * to render, scroll, and interact with. The scroll container is a
 * ``role="listbox"`` div and each row a ``role="option"`` button, with a
 * ``role="presentation"`` sizer between them so the listbox still owns its
 * options in the accessibility tree; because most options are absent from the
 * DOM, each rendered option carries explicit ``aria-setsize`` /
 * ``aria-posinset`` so assistive tech still reports the true position and
 * total (which it would otherwise infer from sibling count).
 *
 * Keyboard model: the listbox itself is the single tab stop (row buttons are
 * ``tabIndex={-1}``). Arrow keys move an active row — reported through
 * ``aria-activedescendant``, scrolled into view via the virtualizer so it is
 * mounted before assistive tech looks it up — Home/End jump to the edges, and
 * Enter or Space toggles the active row.
 */
function OptionListbox({
  options,
  value,
  onToggle,
  listboxRef,
  truncated,
  resultCount,
}: OptionListboxProps) {
  const baseId = React.useId();
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const selectedValues = React.useMemo(
    () => new Set(value.map((option) => option.value)),
    [value],
  );
  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => listboxRef.current,
    estimateSize: () => OPTION_ROW_HEIGHT,
    overscan: OPTION_OVERSCAN,
  });

  // Clamp rather than reset when the list shrinks (e.g. the search narrows),
  // so the active row stays as close as possible to where the user left it.
  const active = Math.min(activeIndex, options.length - 1);

  const moveActive = (next: number) => {
    setActiveIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveActive(Math.min(active + 1, options.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        moveActive(Math.max(active - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        moveActive(0);
        break;
      case "End":
        event.preventDefault();
        moveActive(options.length - 1);
        break;
      case "Enter":
      case " ":
        if (active >= 0) {
          // preventDefault stops Space from scrolling the list and Enter
          // from submitting the enclosing filter form.
          event.preventDefault();
          onToggle(options[active]);
        }
        break;
    }
  };

  return (
    <div
      ref={listboxRef}
      className={styles.list}
      role="listbox"
      aria-label="Filterable values"
      aria-multiselectable="true"
      tabIndex={0}
      aria-activedescendant={
        active >= 0 ? `${baseId}-option-${active}` : undefined
      }
      onKeyDown={handleKeyDown}
      onFocus={() => {
        if (active < 0 && options.length > 0) {
          moveActive(0);
        }
      }}
    >
      <div
        className={styles.sizer}
        role="presentation"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const option = options[virtualRow.index];
          const isSelected = selectedValues.has(option.value);
          return (
            <button
              key={option.value}
              id={`${baseId}-option-${virtualRow.index}`}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={isSelected}
              aria-setsize={options.length}
              aria-posinset={virtualRow.index + 1}
              className={classNames(styles.row, {
                [styles.rowActive]: virtualRow.index === active,
              })}
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              onClick={() => onToggle(option)}
            >
              <Checkbox
                size="small"
                checked={isSelected}
                tabIndex={-1}
                disableRipple
                className={styles.checkbox}
              />
              <span className={styles.rowLabel}>{option.label}</span>
            </button>
          );
        })}
      </div>
      {/* Sticky to the bottom of the SCROLL viewport, and `role="presentation"`
          so a non-option node can't disturb the listbox's ownership of its
          options. */}
      {truncated && (
        <div className={styles.truncationHint} role="presentation">
          <span role="status" aria-live="polite">
            Showing the first {resultCount} matches — refine your search to
            narrow.
          </span>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className={styles.status} role="status" aria-live="polite">
      No matching options
    </div>
  );
}

function ErrorState() {
  return (
    <div className={classNames(styles.status, styles.error)} role="alert">
      Couldn’t load options. Reopen the menu to retry.
    </div>
  );
}
